import { SxProps } from '@mui/material';
import { GRID_DETAIL_PANEL_TOGGLE_FIELD } from '@mui/x-data-grid-pro';

export enum DataGridStyleOverrides {
  CUSTOM_HEADER = 'customHeader',
  DYNAMIC_ROW_HEIGHT = 'dynamicRowHeight',
  CELL_NO_BORDER = 'cellNoBorder',
  ROW_AUTO_HEIGHT = 'rowAutoHeight',
}

export const dataGridStyleOverrides: SxProps = {
  '&.MuiDataGrid-root': {
    border: '0',
    '& .MuiTablePagination-select:focus': { outline: 'solid 1px', outlineColor: 'primary.main' },
    '& .MuiDataGrid-cell, .MuiDataGrid-columnHeader': {
      '& > a': { maxWidth: 1 },
      '&.px-0': { px: '0' },
      /**
       * We want a little more padding than the default MUI value for the first and last columns of
       * tables. This extra padding should not apply to cells with the `px-0` class, nor to the
       * expansion button columns (the inner button itself has a padding)
       */
      [`&:not([data-field="${GRID_DETAIL_PANEL_TOGGLE_FIELD}"]):not(.px-0)`]: {
        '&:first-of-type': { pl: 2 },
        '&:last-of-type': { pr: 2 },
      },
    },
    '& .MuiDataGrid-row': {
      /**
       * Allow dynamically sized rows and inner cells to extend beyond the default height.
       * Preserves the default minimum height to align with other tables.
       */
      [`&.${DataGridStyleOverrides.ROW_AUTO_HEIGHT}`]: {
        minHeight: 52,
        maxHeight: 'unset !important',
        height: 'auto',
        '& .MuiDataGrid-cell': { minHeight: 52, maxHeight: 'none !important', height: 'auto', whiteSpace: 'normal' },
      },
      '&:not(.clickable)': {
        '&:hover, &.Mui-hovered': {
          backgroundColor: 'inherit',
        },
      },
      '&.clickable': {
        cursor: 'pointer',
      },
    },
    '& .MuiDataGrid-cell': {
      [`&.${DataGridStyleOverrides.CELL_NO_BORDER}`]: { border: 'none' },
    },
    '& .MuiDataGrid-columnHeader': {
      '&:focus': { outlineOffset: '-1px' },
    },
    /**
     * For tables that provide a custom header, overrides the default DataGrid
     * styles to prevent the header to be truncated
     */
    [`& .MuiDataGrid-columnHeaders.${DataGridStyleOverrides.CUSTOM_HEADER}`]: {
      maxHeight: 'unset !important',
      '& .MuiDataGrid-columnHeader': {
        height: 'auto !important',
        padding: '0 !important',
        '& .MuiDataGrid-columnHeaderTitleContainerContent': {
          width: 1,
        },
      },
    },
    /**
     * For tables whose rows have a variable height or can be expanded, overrides
     * the default DataGrid styles to prevent content from stuttering while the
     * actual heights are computed using a lazy approach
     */
    [`& .MuiDataGrid-virtualScrollerRenderZone.${DataGridStyleOverrides.DYNAMIC_ROW_HEIGHT}`]: { position: 'relative' },
    [`& .MuiDataGrid-virtualScrollerContent.${DataGridStyleOverrides.DYNAMIC_ROW_HEIGHT}`]: { height: 'auto !important' },
    /**
     * Override the color of the "first page" and "last page" buttons to match "previous" and "next"
     */
    '& .MuiTablePagination-actions button:not(.Mui-disabled)': { color: 'inherit' },
  },
};
